import * as React from 'react';

import { useHead } from '@gitbook/util-react-head';

import { colors } from '../constants';
import { type ColorPalette, makeColorPalette } from '../util';
import type { Variants } from './variants';

/**
 * Design tokens are the visual design atoms of the design system — specifically, they are named entities that store visual design attributes.
 * They can vary depending on the current theme variant. In such a case, we define a default value (to use in case the current theme doesn't match any override) and a set of overrides specifying the value to adopt for a specific variant.
 */
export type Token<T> =
    | DynamicTokenValue<T>
    | {
          value: DynamicTokenValue<T>;
          overrides?: ({
              [variant in keyof typeof Variants]?: (typeof Variants)[variant]['values'][number];
          } & { value: DynamicTokenValue<T> })[];
      };
type DynamicTokenValue<T> = T | ((dynamicTokens: DynamicTokens) => T);

export type DynamicTokens = {
    colors: {
        primary: ColorPalette;
    };
};

export const defaultDynamicTokens: DynamicTokens = {
    colors: {
        primary: {
            xxlight: colors.primary.xxlight,
            xlight: colors.primary.xlight,
            light: colors.primary.light,
            base: colors.primary.base,
            dark: colors.primary.dark,
            xdark: colors.primary.xdark,
            xxdark: colors.primary.xxdark,
        },
    },
};

export type DynamicTokensOverrides = {
    colors: {
        primary: ColorPalette | undefined;
    };
};

export function getDynamicTokens(overrides: DynamicTokensOverrides): DynamicTokens {
    return {
        colors: {
            primary: overrides.colors?.primary ?? defaultDynamicTokens.colors.primary,
        },
    };
}

export const DynamicTokenOverridesContext = React.createContext<DynamicTokensOverrides>({
    colors: {
        primary: undefined,
    },
});

/**
 * Define the primary color for the UI,
 * For example for public content.
 */
export function PrimaryColorTheme(props: {
    /** Primary color to use, if none is passed it'll be reset to the GitBook branding one */
    primaryColor?: string;
    children: React.ReactNode;
}): React.ReactElement {
    const { primaryColor, children } = props;

    const dynamicTokens = React.useContext(DynamicTokenOverridesContext);
    const colorPalette = useColorPalette(primaryColor);

    // inherit all the other tokens
    const newDynamicTokens = React.useMemo(() => {
        return {
            ...dynamicTokens,
            colors: {
                ...dynamicTokens.colors,
                primary: colorPalette,
            },
        };
    }, [dynamicTokens, colorPalette]);

    return (
        <DynamicTokenOverridesContext.Provider value={newDynamicTokens}>
            {children}
        </DynamicTokenOverridesContext.Provider>
    );
}

/**
 * Hook to get a color palette from a color.
 * Side effect: it'll inject a CSS variable in the <head> of the document.
 */
export function useColorPalette(primaryColor: string | undefined) {
    const colorPalette = React.useMemo(
        () => (primaryColor ? makeColorPalette(primaryColor) : undefined),
        [primaryColor]
    );

    // Keep track of the previous base color to ignore the cache when it changes
    const prevBaseColor = React.useRef(colorPalette?.base);

    useHead((head) => {
        if (colorPalette) {
            /**
             * If the base color changes, we need to ignore the cache
             */
            let ignoreCache = false;
            if (colorPalette?.base !== prevBaseColor.current) {
                prevBaseColor.current = colorPalette?.base;
                ignoreCache = true;
            }

            head.setStyles(
                {
                    id: 'root-primary-color',
                    type: 'style',
                    content: getCSSFromColorPalette(colorPalette),
                },
                ignoreCache
            );
        }
    });

    return colorPalette;
}

function getCSSFromColorPalette(colorPalette: ColorPalette) {
    return `:root {
                        ${Object.keys(colorPalette)
                            .map((key) => {
                                return `--custom-theme-color-primary-${key}: ${colorPalette[key]};`;
                            })
                            .join('\n')}
                  }
            `;
}
